import React, { useEffect, useState } from "react";
import { GoogleApiWrapper } from "google-maps-react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { getStateOptions } from "store/selector/general.selector";
import { STATES_LIST } from "Constants/constant";

const AddressAutocomplete = ({
  google,
  children,
  customNames = {},
  addressId = "address-input",
  isStateName = false,
}) => {
  const formContext = useFormContext();
  const setValue = formContext?.setValue;
  const clearErrors = formContext?.clearErrors;
  const statesOptions = useSelector(getStateOptions);

  const [stateOps, setStateOps] = useState(statesOptions);

  useEffect(() => {
    if (statesOptions && statesOptions.length > 0) {
      setStateOps(statesOptions);
    }
  }, [statesOptions]);

  useEffect(() => {
    // Use google from props (from GoogleApiWrapper) or fallback to window.google
    const getGoogleMaps = () => google || window?.google;
    
    // Function to initialize autocomplete
    const initializeAutocomplete = (googleMaps) => {
      if (!googleMaps || !googleMaps.maps) {
        console.warn("Google Maps not available");
        return;
      }

      if (!googleMaps.maps.places) {
        console.warn("Google Places API not available. Make sure Places API is enabled in your Google Cloud Console and the API key has Places API access.");
        return;
      }

      // If isStateName is true, we don't need state options (we use state name directly)
      // Otherwise, we need state options to map state name to state ID
      // For now, we'll proceed even if state options aren't loaded - they'll be used when available
      if (!isStateName && stateOps.length === 0) {
        console.warn("State options not loaded yet, but proceeding with autocomplete initialization");
        // Continue anyway - state options will be used when available during place selection
      }

      // Wait a bit for the input to be rendered
      let retryCount = 0;
      const maxRetries = 10;
      
      const initAutocomplete = () => {
      const input = document.getElementById(
        addressId !== null ? addressId : "address-input"
      );

      if (!input) {
        // Retry after a short delay if input not found (max 10 retries = 1 second)
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initAutocomplete, 100);
        } else {
          console.error(`Input field with id "${addressId || 'address-input'}" not found after ${maxRetries} retries`);
        }
        return;
      }

      // Check if autocomplete is already attached to avoid duplicates
      if (input.dataset.autocompleteAttached === "true") {
        return;
      }

      try {
        const options = {
          componentRestrictions: { country: "au" },
          fields: ["address_components", "geometry", "formatted_address"],
        };

        const autocompleteService = new googleMaps.maps.places.Autocomplete(
          input,
          options
        );

        // Mark as attached
        input.dataset.autocompleteAttached = "true";

        autocompleteService.addListener("place_changed", () => {
          const place = autocompleteService.getPlace();

          if (place) {
            let street = "";
            let suburb = "";
            let state = "";
            let postcode = "";
            let country = "";
            let lat = null;
            let lng = null;

            // Get lat/lng from place geometry
            if (place.geometry && place.geometry.location) {
              lat = place.geometry.location.lat();
              lng = place.geometry.location.lng();
            }

            // Map country codes to full country names
            const countryNameMap = {
              au: "Australia",
              us: "United States",
              gb: "United Kingdom",
              ca: "Canada",
              nz: "New Zealand",
              // Add more as needed
            };

            place.address_components.forEach((component) => {
              const types = component.types;

              if (types.includes("street_number") || types.includes("route")) {
                street += component.long_name + " ";
              } else if (types.includes("locality")) {
                suburb = component.long_name;
              } else if (types.includes("administrative_area_level_1")) {
                // Google Places API returns state in short_name (e.g., "VIC", "NSW", "QLD")
                // and long_name (e.g., "Victoria", "New South Wales", "Queensland")
                // Always prefer short_name (state code) for consistent matching
                // If short_name is not available, fall back to long_name
                if (component.short_name) {
                  state = component.short_name;
                } else if (component.long_name) {
                  state = component.long_name;
                }
              } else if (types.includes("postal_code")) {
                postcode = component.long_name;
              } else if (types.includes("country")) {
                // Get country code and map to full name
                const countryCode = component.short_name.toLowerCase();
                country = countryNameMap[countryCode] || component.long_name;
              }
            });
            
            if (setValue) {
              setValue(
                customNames?.street_address
                  ? customNames?.street_address
                  : "street_address",
                street?.trim()
              );
              setValue(
                customNames?.suburb ? customNames?.suburb : "suburb",
                suburb
              );
              // Handle state value setting
              // Always try to map state to STATES_LIST value for consistency
              // This ensures the state dropdown is populated correctly for all states
              let stateValue = null;
              
              if (state) {
                // Normalize the state string for better matching
                const normalizedState = state.trim().toLowerCase();
                
                // Google Places API returns state as short code (e.g., "VIC", "NSW", "QLD") 
                // or full name (e.g., "Victoria", "New South Wales", "Queensland")
                // STATES_LIST structure: { label: "VIC", value: "Victoria" }
                // SelectOption matches by value, so we need to set the full name
                
                // Strategy 1: Exact match by label (short code like "VIC", "QLD", "NSW")
                let statesListMatch = STATES_LIST.find(
                  ({ label }) => label && label.toLowerCase() === normalizedState
                );
                
                // Strategy 2: Exact match by value (full name like "Victoria", "Queensland")
                if (!statesListMatch) {
                  statesListMatch = STATES_LIST.find(
                    ({ value }) => value && value.toLowerCase() === normalizedState
                  );
                }
                
                // Strategy 3: Case-insensitive partial matching for variations
                // Handles cases like "New South Wales" vs "New south Wales"
                if (!statesListMatch) {
                  statesListMatch = STATES_LIST.find(({ value, label }) => {
                    if (!value || !label) return false;
                    
                    const normalizedValue = value.toLowerCase().replace(/\s+/g, '');
                    const normalizedLabel = label.toLowerCase();
                    const normalizedInput = normalizedState.replace(/\s+/g, '');
                    
                    // Exact match after removing spaces
                    if (normalizedValue === normalizedInput || normalizedLabel === normalizedInput) {
                      return true;
                    }
                    
                    // Partial match - check if input contains value or vice versa
                    if (normalizedValue.includes(normalizedInput) || normalizedInput.includes(normalizedValue)) {
                      return true;
                    }
                    
                    // Check if label matches input
                    if (normalizedLabel === normalizedInput) {
                      return true;
                    }
                    
                    return false;
                  });
                }
                
                // Strategy 4: Word-by-word matching for multi-word states
                // Handles "New South Wales" matching "New south Wales"
                if (!statesListMatch && normalizedState.includes(' ')) {
                  const inputWords = normalizedState.split(/\s+/).filter(w => w.length > 0);
                  statesListMatch = STATES_LIST.find(({ value }) => {
                    if (!value) return false;
                    const valueWords = value.toLowerCase().split(/\s+/).filter(w => w.length > 0);
                    // Check if all input words are in value words (order doesn't matter)
                    return inputWords.every(word => 
                      valueWords.some(vWord => vWord === word || vWord.includes(word) || word.includes(vWord))
                    );
                  });
                }
                
                if (statesListMatch) {
                  // Use the value (full name) from STATES_LIST so SelectOption can match it
                  stateValue = statesListMatch.value;
                } else if (stateOps && stateOps.length > 0) {
                  // Fallback: try to find in stateOps by name or label
                  const foundState = stateOps.find(
                    ({ name, label }) => {
                      const nameMatch = name && name.toLowerCase() === normalizedState;
                      const labelMatch = label && label.toLowerCase() === normalizedState;
                      return nameMatch || labelMatch;
                    }
                  );
                  
                  if (foundState) {
                    // Map stateOps back to STATES_LIST value
                    const stateName = foundState.name || foundState.label;
                    const mappedState = STATES_LIST.find(
                      ({ label, value }) => {
                        if (!label || !value) return false;
                        return label.toLowerCase() === stateName?.toLowerCase() ||
                               value.toLowerCase() === stateName?.toLowerCase();
                      }
                    );
                    stateValue = mappedState?.value || stateName;
                  }
                }
                
                // Final fallback: if no match found, use original state
                // This ensures the field is always populated
                if (!stateValue) {
                  stateValue = state.trim();
                }
              }
              
              // Set the state value - ensure it's always set
              const stateFieldName = customNames?.state ? customNames?.state : "state_id";
              
              // If stateValue was successfully mapped to STATES_LIST, use it
              // Otherwise, fall back to the original state from Google Places
              // This ensures the state field is always populated
              const finalStateValue = stateValue || state || "";
              
              // Use setValue with options to trigger validation and update form state
              setValue(stateFieldName, finalStateValue, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
              });
              
              // Debug: Log state mapping for troubleshooting
              if (state) {
                if (stateValue && stateValue !== state.trim()) {
                  // Successfully mapped
                  console.log("AddressAutocomplete: State mapped successfully", {
                    originalState: state,
                    mappedState: stateValue,
                    finalStateValue: finalStateValue,
                    stateFieldName: stateFieldName
                  });
                } else if (!stateValue || stateValue === state.trim()) {
                  // Not mapped or using original
                  console.warn("AddressAutocomplete: State not mapped to STATES_LIST", {
                    originalState: state,
                    normalizedState: state.trim().toLowerCase(),
                    finalStateValue: finalStateValue,
                    stateFieldName: stateFieldName,
                    availableStates: STATES_LIST.map(s => `${s.label} -> ${s.value}`)
                  });
                }
              }
              setValue(
                customNames?.postcode ? customNames?.postcode : "postal_code",
                postcode
              );
              
              // Set country if available
              if (country) {
                setValue(
                  customNames?.country ? customNames?.country : "country",
                  country
                );
              }
              
              // Set lat/lng if provided in customNames
              if (lat !== null && lng !== null) {
                if (customNames?.lat) {
                  setValue(customNames.lat, lat);
                }
                if (customNames?.lng) {
                  setValue(customNames.lng, lng);
                }
              }
              
              if (clearErrors && typeof clearErrors === 'function') {
                clearErrors([
                  customNames?.street_address
                    ? customNames?.street_address
                    : "street_address",
                  customNames?.suburb ? customNames?.suburb : "suburb",
                  customNames?.state ? customNames?.state : "state_id",
                  customNames?.postcode ? customNames?.postcode : "postal_code",
                  customNames?.country ? customNames?.country : "country",
                ]);
              }
            }
          }
        });
      } catch (error) {
        console.error("Error initializing Google Places Autocomplete:", error);
        input.dataset.autocompleteAttached = "false"; // Allow retry on error
      }
    };

      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(initAutocomplete, 100);
      
      return () => {
        clearTimeout(timeoutId);
      };
    };
    
    // Check if Google Maps API is loaded, if not wait and retry
    let googleMaps = getGoogleMaps();
    
    if (!googleMaps) {
      console.warn("Google Maps API not loaded yet. Waiting...");
      // Retry mechanism to wait for Google Maps to load
      let checkCount = 0;
      const maxChecks = 30; // Check for up to 3 seconds
      const checkInterval = setInterval(() => {
        checkCount++;
        googleMaps = getGoogleMaps();
        
        if (googleMaps && googleMaps.maps && googleMaps.maps.places) {
          clearInterval(checkInterval);
          initializeAutocomplete(googleMaps);
        } else if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
          console.error("Google Maps API failed to load after multiple attempts. Make sure REACT_APP_GOOGLE_MAPS_API_KEY is set and Places API is enabled.");
        }
      }, 100);
      
      return () => clearInterval(checkInterval);
    }

    // Google Maps is already loaded, initialize immediately
    return initializeAutocomplete(googleMaps);
  }, [google, stateOps, setValue, clearErrors, addressId, customNames, isStateName]);

  return <>{children}</>;
};

export default GoogleApiWrapper({
  apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
})(AddressAutocomplete);
